import { useEffect } from 'react';
import type { useUpdateScene } from '../../../../features/scenarios/hooks';
import type { Scenario } from '../types';

type LastFrameData = { url: string; sourceUrl?: string; materialId: string; width?: number; height?: number } | null;

interface UseFirstFrameSyncParams {
  scenarios: Scenario[];
  scenePreviewIndex: Record<string, number>;
  scenePreviewMaterials: Record<string, any[]>;
  sceneLastFrames: Record<string, LastFrameData>;
  setSceneLastFrames: (updater: (prev: Record<string, LastFrameData>) => Record<string, LastFrameData>) => void;
  updateSceneMutation: ReturnType<typeof useUpdateScene>;
}

// 「確定された素材を selectedAssets に自動同期する」effect は廃止した
// (docs/features/link-previous-scene-continuity-default-plan.md §9 step-1 項目8・
// 急所2「selectedAssets 分離」)。素材欄への自動挿入・動画生成への混入は行わず、
// 場面転換シーンのフレーム生成にだけ直前の確定フレームを内部参照として渡す
// (useFrameHandlers.ts の handleGenerateScenePreview を参照。selectedAssets には
// 一切追加しない)。旧仕様で silent 追加された既存データの移行処理は
// useScenarioGeneration.ts の handleLoadScenario 側で行う。
export function useFirstFrameSync({
  scenarios,
  scenePreviewIndex,
  scenePreviewMaterials,
  sceneLastFrames,
  setSceneLastFrames,
  updateSceneMutation,
}: UseFirstFrameSyncParams): void {
  // first_with_next モード: 次シーンの「最初のフレーム」の表示変更を
  // 前シーンの「最後のフレーム」に反映する
  // auto_appendモードのみ動作（generate_lastモードでは独自に生成するため上書きしない）
  useEffect(() => {
    if (!scenarios.length) return;

    scenarios.forEach((scenario) => {
      // first_with_nextモードのシナリオのみ処理
      if (scenario.frame_mode !== 'first_with_next') return;
      scenario.scenes.forEach((scene) => {
        // シーン2以降のみ処理
        if (scene.order <= 1) return;

        const key = `${scenario.id}-scene-${scene.order}`;
        const currentIndex = scenePreviewIndex[key] || 0;
        const materials = scenePreviewMaterials[key] || [];
        const currentMaterial = materials[currentIndex];

        // 前のシーンを取得
        const prevScene = scenario.scenes.find((s) => s.order === scene.order - 1);
        if (!prevScene) return;

        const prevKey = `${scenario.id}-scene-${prevScene.order}`;
        const prevLastFrame = sceneLastFrames[prevKey];

        // 前のシーンの最後のフレームが既に設定されている場合のみ更新
        if (!prevLastFrame) return;

        // 現在表示されている画像を取得（サムネイルは最後のフレームに使用しない）
        if (!currentMaterial) return;

        const imageUrl =
          currentMaterial.source_url ||
          currentMaterial.sourceUrl ||
          currentMaterial.preview_url ||
          currentMaterial.previewUrl;

        if (!imageUrl) return;

        // 既に同じ画像が設定されている場合はスキップ
        if (prevLastFrame.url === imageUrl) return;

        // 最後のフレームを更新
        setSceneLastFrames((prev) => ({
          ...prev,
          [prevKey]: {
            url: imageUrl,
            sourceUrl: imageUrl,
            materialId: currentMaterial.id || '',
          },
        }));

        // バックエンドに保存（非同期で実行、エラーは無視）
        if (!prevScene.id) return;
        updateSceneMutation
          .mutateAsync({
            sceneId: prevScene.id,
            payload: {
              last_frame_url: imageUrl,
              last_frame_material_id: currentMaterial.id || undefined,
            },
          })
          .catch((error) => {
            console.error('最後のフレームの自動更新に失敗:', error);
          });
      });
    });
  }, [scenePreviewIndex, scenarios, scenePreviewMaterials, sceneLastFrames, updateSceneMutation, setSceneLastFrames]);
}
